import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, Search, Trash2, Menu, X, CheckCircle, Clock,
  Database, Globe, Zap, User, Briefcase, FileText,
  MessageSquare, Mail, DollarSign, Calendar, Phone, Package,
  Building2, Tag, ChevronRight, LayoutGrid, History,
  CreditCard, Receipt, Link2, Star, Megaphone, Bot,
  MessageCircle, Users, BarChart3, Settings, UserPlus,
  GitBranch, Send, Eye, PieChart, TrendingUp,
  AlertTriangle, Palette, Filter, Shield, HelpCircle, Info
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { toast } from 'sonner';
import { WizardController } from '../components/agent/TaskWizards';
import { WorkspaceLaunchCard } from '../components/WorkspaceLaunchCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Wizard categories and items
const WIZARD_CATEGORIES = [
  // Leads Section (Most Important Functions - First!)
  {
    id: 'leads',
    name: 'Leads',
    description: 'Essential lead management actions',
    icon: User,
    color: 'silver',
    wizards: [
      { id: 'create_lead', name: 'Create Lead', description: 'Add a new lead to your CRM', icon: UserPlus, status: 'active' },
      { id: 'search_lead', name: 'Search Lead', description: 'Find a lead in your CRM', icon: Search, status: 'active' },
      { id: 'call_lead', name: 'Call Lead', description: 'Call a lead directly', icon: Phone, status: 'active' },
      { id: 'text_lead', name: 'Text Lead', description: 'Send SMS to a lead', icon: MessageSquare, status: 'active' },
      { id: 'email_lead', name: 'Email Lead', description: 'Send email to a lead', icon: Mail, status: 'active' },
      { id: 'book_lead_appointment', name: 'Book Appointment', description: 'Schedule appointment with lead', icon: Calendar, status: 'active' },
      { id: 'send_lead_agreement', name: 'Send Agreement', description: 'Send agreement to lead', icon: FileText, status: 'active' },
      { id: 'send_lead_estimate', name: 'Send Estimate', description: 'Send estimate to lead', icon: Receipt, status: 'active' },
      { id: 'send_lead_invoice', name: 'Send Invoice', description: 'Send invoice to lead', icon: Receipt, status: 'active' },
      { id: 'send_lead_payment_link', name: 'Send Payment Link', description: 'Send payment link to lead', icon: Link2, status: 'active' },
      { id: 'send_lead_subscription', name: 'Send Subscription', description: 'Set up recurring payment', icon: CreditCard, status: 'active' },
      { id: 'search_lead_pipelines', name: 'Search Lead Pipelines', description: 'View leads by pipeline stage', icon: GitBranch, status: 'active' },
    ]
  },
  // Core CRM
  {
    id: 'crm',
    name: 'CRM',
    description: 'Contact & customer management',
    icon: Database,
    color: 'cyan',
    wizards: [
      { id: 'search_contact', name: 'Search Contact', description: 'Find and view contact details', icon: Search, status: 'active' },
      { id: 'create_contact', name: 'Create Contact', description: 'Add a new contact to your CRM', icon: User, status: 'active' },
      { id: 'update_contact', name: 'Update Contact', description: 'Update an existing contact', icon: User, status: 'active' },
      { id: 'add_note_task', name: 'Add Note/Task', description: 'Add a note or task to a contact', icon: FileText, status: 'active' },
      { id: 'send_sms', name: 'Send SMS', description: 'Send an SMS to a contact', icon: MessageSquare, status: 'active' },
      { id: 'send_email', name: 'Send Email', description: 'Send an email to a contact', icon: Mail, status: 'active' },
      { id: 'create_opportunity', name: 'Create Opportunity', description: 'Create a new deal/opportunity', icon: DollarSign, status: 'active' },
      { id: 'move_opportunity', name: 'Move Opportunity Stage', description: 'Move a deal to a different stage', icon: Briefcase, status: 'active' },
      { id: 'create_appointment', name: 'Create Appointment', description: 'Schedule an appointment', icon: Calendar, status: 'active' },
      { id: 'workflow_deploy', name: 'Deploy Workflow Recipe', description: 'Deploy an automation recipe', icon: Zap, status: 'active' },
      { id: 'purchase_phone', name: 'Purchase Phone Number', description: 'Buy a new phone number', icon: Phone, status: 'active' },
    ]
  },
  // G) Payments / Billing / Proposals
  {
    id: 'payments',
    name: 'Payments & Billing',
    description: 'Invoices, proposals & payment links',
    icon: CreditCard,
    color: 'green',
    wizards: [
      { id: 'send_proposal', name: 'Send Proposal', description: 'Create and send a proposal', icon: FileText, status: 'active' },
      { id: 'create_invoice', name: 'Create & Send Invoice', description: 'Create and send an invoice', icon: Receipt, status: 'active' },
      { id: 'create_payment_link', name: 'Create Payment Link', description: 'Generate a payment link', icon: Link2, status: 'active' },
    ]
  },
  // H) Reviews / Reputation
  {
    id: 'reviews',
    name: 'Reviews & Reputation',
    description: 'Review requests & campaigns',
    icon: Star,
    color: 'amber',
    wizards: [
      { id: 'send_review_request', name: 'Send Review Request', description: 'Request a review from a contact', icon: Star, status: 'active' },
      { id: 'create_review_campaign', name: 'Create Review Campaign', description: 'Set up automated review requests', icon: Megaphone, status: 'active' },
    ]
  },
  // I) AI / Chatbots / Automations
  {
    id: 'ai_automation',
    name: 'AI & Automation',
    description: 'Chatbots, widgets & AI agents',
    icon: Bot,
    color: 'purple',
    wizards: [
      { id: 'create_chat_widget', name: 'Create Chat Widget', description: 'Set up a website chat widget', icon: MessageCircle, status: 'active' },
      { id: 'create_ai_chatbot', name: 'Create AI Chatbot', description: 'Build an AI-powered chatbot', icon: Bot, status: 'active' },
      { id: 'create_ai_agent', name: 'Create AI Agent', description: 'Deploy advanced AI automation', icon: Zap, status: 'active' },
    ]
  },
  // J) Conversations / Team Communication
  {
    id: 'conversations',
    name: 'Conversations',
    description: 'Team messaging & conversation management',
    icon: MessageCircle,
    color: 'blue',
    wizards: [
      { id: 'send_internal_message', name: 'Send Internal Message', description: 'Message a team member', icon: Send, status: 'active' },
      { id: 'check_unread_messages', name: 'Check Unread Messages', description: 'View unread conversations', icon: Eye, status: 'active' },
      { id: 'assign_conversation', name: 'Assign Conversation', description: 'Assign a conversation to team', icon: Users, status: 'active' },
    ]
  },
  // K) Reporting / Dashboards
  {
    id: 'reporting',
    name: 'Reporting',
    description: 'Analytics, reports & dashboards',
    icon: BarChart3,
    color: 'cyan',
    wizards: [
      { id: 'dashboard_snapshot', name: 'Dashboard Snapshot', description: 'Get a quick metrics overview', icon: PieChart, status: 'active' },
      { id: 'revenue_report', name: 'Revenue Report', description: 'View revenue analytics', icon: TrendingUp, status: 'active' },
      { id: 'campaign_report', name: 'Campaign Performance', description: 'Analyze campaign results', icon: BarChart3, status: 'active' },
    ]
  },
  // L) Customization / Admin Tools
  {
    id: 'admin',
    name: 'Admin Tools',
    description: 'Custom fields, tags, users & pipelines',
    icon: Settings,
    color: 'gray',
    wizards: [
      { id: 'create_custom_field', name: 'Create Custom Field', description: 'Add a custom field to CRM', icon: Palette, status: 'active' },
      { id: 'create_tag', name: 'Create Tag', description: 'Create a new tag', icon: Tag, status: 'active' },
      { id: 'create_user', name: 'Create User', description: 'Add a new team member', icon: UserPlus, status: 'active' },
      { id: 'create_pipeline', name: 'Create Pipeline', description: 'Build a new sales pipeline', icon: GitBranch, status: 'active' },
    ]
  },
  // M) Bulk Actions
  {
    id: 'bulk',
    name: 'Bulk Actions',
    description: 'Mass operations (require confirmation)',
    icon: Filter,
    color: 'red',
    wizards: [
      { id: 'bulk_sms', name: 'Bulk SMS', description: 'Send SMS to multiple contacts', icon: MessageSquare, status: 'active' },
      { id: 'bulk_tag', name: 'Bulk Tag Update', description: 'Add/remove tags in bulk', icon: Tag, status: 'active' },
    ]
  },
  // POD
  {
    id: 'pod',
    name: 'POD',
    description: 'Partner on Demand orders',
    icon: Package,
    color: 'purple',
    wizards: [
      { id: 'create_pod_order', name: 'Create POD Order', description: 'Submit a new POD order', icon: Package, status: 'active' },
      { id: 'revision_request', name: 'Request Revision', description: 'Request revisions on an order', icon: FileText, status: 'active' },
      { id: 'order_status', name: 'Order Status/Escalation', description: 'Check or escalate an order', icon: Clock, status: 'active' },
      { id: 'asset_generator', name: 'Asset Generator', description: 'Generate marketing assets', icon: Zap, status: 'active' },
    ]
  },
  // Directory
  {
    id: 'directory',
    name: 'Directory',
    description: 'Business directory listings',
    icon: Globe,
    color: 'green',
    wizards: [
      { id: 'create_listing', name: 'Create Listing', description: 'Create a new business listing', icon: Building2, status: 'active' },
      { id: 'update_listing', name: 'Update Listing', description: 'Update an existing listing', icon: Building2, status: 'active' },
      { id: 'add_offer', name: 'Add Offer/Lead Magnet', description: 'Add an offer to your listing', icon: Tag, status: 'active' },
      { id: 'listing_report', name: 'Performance Report', description: 'View listing analytics', icon: TrendingUp, status: 'active' },
    ]
  },
  // Power Workhorses
  {
    id: 'power',
    name: 'Power Workhorses',
    description: 'Cross-system automations',
    icon: Zap,
    color: 'amber',
    wizards: [
      { id: 'directory_lead_followup', name: 'Directory Lead Follow-up', description: 'Auto-route directory leads to CRM', icon: Globe, status: 'active' },
      { id: 'new_client_setup', name: 'New Client Setup', description: 'Complete client onboarding', icon: User, status: 'active' },
    ]
  }
];

// Map wizard_type to wizard_id
const WIZARD_TYPE_MAP = {
  // Sales Lead
  'LEAD.CREATE': 'create_lead',
  'LEAD.SEARCH': 'search_lead',
  'LEAD.CALL': 'call_lead',
  'LEAD.TEXT': 'text_lead',
  'LEAD.EMAIL': 'email_lead',
  'LEAD.BOOK_APPOINTMENT': 'book_lead_appointment',
  'LEAD.SEND_AGREEMENT': 'send_lead_agreement',
  'LEAD.SEND_ESTIMATE': 'send_lead_estimate',
  'LEAD.SEND_INVOICE': 'send_lead_invoice',
  'LEAD.SEND_PAYMENT_LINK': 'send_lead_payment_link',
  'LEAD.SEND_SUBSCRIPTION': 'send_lead_subscription',
  'LEAD.SEARCH_PIPELINES': 'search_lead_pipelines',
  // Core CRM
  'CRM.CREATE_CONTACT': 'create_contact',
  'CRM.UPDATE_CONTACT': 'update_contact',
  'CRM.SEND_SMS': 'send_sms',
  'CRM.SEND_EMAIL': 'send_email',
  'CRM.ADD_NOTE_TASK': 'add_note_task',
  'CRM.CREATE_OPPORTUNITY': 'create_opportunity',
  'CRM.MOVE_OPPORTUNITY': 'move_opportunity',
  'CRM.CREATE_APPOINTMENT': 'create_appointment',
  'CRM.WORKFLOW_DEPLOY': 'workflow_deploy',
  'CRM.PURCHASE_PHONE': 'purchase_phone',
  // Payments / Billing
  'CRM.PROPOSAL_CREATE': 'send_proposal',
  'CRM.INVOICE_CREATE': 'create_invoice',
  'CRM.PAYMENT_LINK_CREATE': 'create_payment_link',
  // Reviews / Reputation
  'CRM.REVIEW_REQUEST_SEND': 'send_review_request',
  'CRM.REVIEW_CAMPAIGN_CREATE': 'create_review_campaign',
  // AI / Chatbots / Automations
  'CRM.CHAT_WIDGET_CREATE': 'create_chat_widget',
  'CRM.AI_CHATBOT_CREATE': 'create_ai_chatbot',
  'CRM.AI_AGENT_DEPLOY': 'create_ai_agent',
  // Conversations
  'CRM.INTERNAL_MESSAGE_SEND': 'send_internal_message',
  'CRM.CONVERSATIONS_UNREAD_FETCH': 'check_unread_messages',
  'CRM.CONVERSATION_ASSIGN': 'assign_conversation',
  // Reporting
  'CRM.DASHBOARD_REPORT': 'dashboard_snapshot',
  'CRM.REVENUE_REPORT': 'revenue_report',
  'CRM.CAMPAIGN_REPORT': 'campaign_report',
  // Admin Tools
  'CRM.CUSTOM_FIELD_CREATE': 'create_custom_field',
  'CRM.TAG_CREATE': 'create_tag',
  'CRM.USER_CREATE': 'create_user',
  'CRM.PIPELINE_CREATE': 'create_pipeline',
  // Bulk Actions
  'CRM.SMS_BULK_SEND': 'bulk_sms',
  'CRM.BULK_TAG_UPDATE': 'bulk_tag',
  // POD
  'POD.CREATE_ORDER': 'create_pod_order',
  'POD.REVISION_REQUEST': 'revision_request',
  'POD.ORDER_STATUS': 'order_status',
  'POD.ASSET_GENERATOR': 'asset_generator',
  // Directory
  'DIRECTORY.CREATE_LISTING': 'create_listing',
  'DIRECTORY.UPDATE_LISTING': 'update_listing',
  'DIRECTORY.ADD_OFFER': 'add_offer',
  'DIRECTORY.LISTING_REPORT': 'listing_report',
  // Power Wizards
  'POWER.DIRECTORY_LEAD_FOLLOWUP': 'directory_lead_followup',
  'POWER.NEW_CLIENT_SETUP': 'new_client_setup',
};

// Helper function to format time ago
function formatTimeAgo(dateStr) {
  if (!dateStr) return 'unknown';
  const date = new Date(dateStr);
  const now = new Date();
  const diff = (now - date) / 1000;
  
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
  return `${Math.floor(diff / 86400)} days ago`;
}

// Get color classes for category
const getCategoryColor = (color) => {
  const colors = {
    cyan: { bg: 'bg-cyan-500/10', border: 'border-cyan-500/20', text: 'text-cyan-400', hover: 'hover:bg-cyan-500/20' },
    purple: { bg: 'bg-purple-500/10', border: 'border-purple-500/20', text: 'text-purple-400', hover: 'hover:bg-purple-500/20' },
    green: { bg: 'bg-green-500/10', border: 'border-green-500/20', text: 'text-green-400', hover: 'hover:bg-green-500/20' },
    amber: { bg: 'bg-amber-500/10', border: 'border-amber-500/20', text: 'text-amber-400', hover: 'hover:bg-amber-500/20' },
    blue: { bg: 'bg-blue-500/10', border: 'border-blue-500/20', text: 'text-blue-400', hover: 'hover:bg-blue-500/20' },
    red: { bg: 'bg-red-500/10', border: 'border-red-500/20', text: 'text-red-400', hover: 'hover:bg-red-500/20' },
    gray: { bg: 'bg-gray-500/10', border: 'border-gray-500/20', text: 'text-gray-400', hover: 'hover:bg-gray-500/20' },
    orange: { bg: 'bg-orange-500/10', border: 'border-orange-500/20', text: 'text-orange-400', hover: 'hover:bg-orange-500/20' },
    gold: { bg: 'bg-yellow-500/10', border: 'border-yellow-400/30', text: 'text-yellow-400', hover: 'hover:bg-yellow-500/20' },
    silver: { bg: 'bg-slate-400/10', border: 'border-slate-400/30', text: 'text-slate-300', hover: 'hover:bg-slate-400/20' },
  };
  return colors[color] || colors.cyan;
};

// Get auth headers
const getAuthHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json'
});

export default function AgentPage() {
  // State
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(typeof window !== 'undefined' && window.innerWidth >= 768);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 768);
  const [searchQuery, setSearchQuery] = useState('');
  const [showLearnMore, setShowLearnMore] = useState(false);
  
  // Wizard state
  const [activeWizard, setActiveWizard] = useState(null);
  const [wizardInitialData, setWizardInitialData] = useState({});
  
  // Drafts and history
  const [drafts, setDrafts] = useState([]);
  const [taskHistory, setTaskHistory] = useState([]);
  
  // Connection status
  const [connections, setConnections] = useState({
    crm: false,
    pod: false,
    directory: false
  });
  
  // Responsive handling
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) setSidebarOpen(true);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Fetch connection status
  useEffect(() => {
    const fetchConnections = async () => {
      try {
        const response = await fetch(`${API_URL}/api/integrations/status`, {
          headers: getAuthHeaders()
        });
        if (response.ok) {
          const data = await response.json();
          setConnections({
            crm: data.ghl_connected || false,
            pod: data.pod_connected || true, // POD is always available
            directory: data.directory_connected || true // Directory is always available
          });
        }
      } catch (error) {
        console.error('Error fetching connections:', error);
      }
    };
    fetchConnections();
  }, []);
  
  // Fetch drafts and task history
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch drafts
      const draftsRes = await fetch(`${API_URL}/api/wizards/wizard-drafts`, {
        headers: getAuthHeaders()
      });
      if (draftsRes.ok) {
        const data = await draftsRes.json();
        setDrafts(data.drafts || []);
      }
      
      // Fetch task history (completed wizard tasks from audit log)
      const historyRes = await fetch(`${API_URL}/api/agent/task-history?limit=50`, {
        headers: getAuthHeaders()
      });
      if (historyRes.ok) {
        const data = await historyRes.json();
        setTaskHistory(data.tasks || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  // Delete draft
  const deleteDraft = async (draftId) => {
    try {
      const response = await fetch(`${API_URL}/api/wizards/wizard-drafts/${draftId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        setDrafts(prev => prev.filter(d => d.id !== draftId));
        toast.success('Draft deleted');
      }
    } catch (error) {
      toast.error('Failed to delete draft');
    }
  };
  
  // Handle wizard selection
  const handleWizardSelect = (wizardId, draft = null) => {
    // Map wizard_type to wizard_id if from draft
    let actualWizardId = wizardId;
    if (draft && draft.wizard_type) {
      actualWizardId = WIZARD_TYPE_MAP[draft.wizard_type] || wizardId;
    }
    
    setActiveWizard(actualWizardId);
    setWizardInitialData(draft?.form_data_json || {});
  };
  
  // Handle wizard success
  const handleWizardSuccess = (result) => {
    fetchData(); // Refresh drafts and history
    toast.success('Task completed successfully');
  };
  
  // Close wizard
  const closeWizard = () => {
    setActiveWizard(null);
    setWizardInitialData({});
  };
  
  // Filter wizards by search query
  const filteredCategories = WIZARD_CATEGORIES.map(cat => ({
    ...cat,
    wizards: cat.wizards.filter(w => 
      w.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      w.description.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(cat => cat.wizards.length > 0 || searchQuery === '');
  
  // Get wizard name for display
  const getWizardName = (wizardType) => {
    const wizardId = WIZARD_TYPE_MAP[wizardType];
    for (const cat of WIZARD_CATEGORIES) {
      const wizard = cat.wizards.find(w => w.id === wizardId);
      if (wizard) return wizard.name;
    }
    return wizardType?.replace('CRM.', '').replace('POD.', '').replace('DIRECTORY.', '').replace('_', ' ') || 'Unknown';
  };

  return (
    <DashboardLayout>
      <div className="flex h-[calc(100vh-80px)] overflow-hidden bg-[#0a0a0f]">
        {/* Mobile Menu Toggle */}
        {isMobile && (
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="fixed top-20 left-4 z-50 p-2 rounded-lg bg-[#12121a] border border-white/10 md:hidden"
            data-testid="mobile-menu-toggle"
          >
            {sidebarOpen ? <X className="w-5 h-5 text-white" /> : <Menu className="w-5 h-5 text-white" />}
          </button>
        )}
        
        {/* Sidebar - Drafts & Task History */}
        <AnimatePresence>
          {sidebarOpen && (
            <motion.aside
              initial={isMobile ? { x: -300 } : { x: 0 }}
              animate={{ x: 0 }}
              exit={{ x: -300 }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className={cn(
                "w-72 bg-[#12121a] border-r border-white/10 flex flex-col z-40 pt-4",
                isMobile ? "fixed left-0 top-16 h-[calc(100vh-64px)]" : "relative"
              )}
            >
              {/* Hero Video - Robot Horse */}
              <div className="px-3 pt-2 pb-2">
                <div className="relative rounded-xl overflow-hidden">
                  <video 
                    src="https://customer-assets.emergentagent.com/job_6e360c3b-e6a7-43c8-9e07-36a091b1723b/artifacts/gzca41jb_kling_20260214_Image_to_Video_Horse_has__2187_0.mp4"
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="w-full h-auto object-cover"
                    style={{
                      maskImage: 'linear-gradient(to bottom, black 70%, transparent 100%)',
                      WebkitMaskImage: 'linear-gradient(to bottom, black 70%, transparent 100%)'
                    }}
                  />
                </div>
              </div>
              
              {/* Connection Status - Compact Row */}
              <div className="px-4 pb-4 border-b border-white/10">
                <div className="flex items-center justify-between gap-2">
                  <a
                    href="/settings?tab=integrations"
                    className={cn(
                      "flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium transition-colors",
                      connections.crm 
                        ? "bg-green-500/10 text-green-400 hover:bg-green-500/20" 
                        : "bg-white/5 text-gray-400 hover:bg-white/10"
                    )}
                    title={connections.crm ? 'CRM Connected' : 'CRM Not Connected'}
                  >
                    <div className={cn("w-1.5 h-1.5 rounded-full", connections.crm ? "bg-green-400" : "bg-gray-500")} />
                    <Database className="w-3 h-3" />
                    <span>CRM</span>
                  </a>
                  <div className={cn(
                    "flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium",
                    connections.pod 
                      ? "bg-green-500/10 text-green-400" 
                      : "bg-white/5 text-gray-400"
                  )}
                    title={connections.pod ? 'POD Connected' : 'POD Not Connected'}
                  >
                    <div className={cn("w-1.5 h-1.5 rounded-full", connections.pod ? "bg-green-400" : "bg-gray-500")} />
                    <Package className="w-3 h-3" />
                    <span>POD</span>
                  </div>
                  <div className={cn(
                    "flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium",
                    connections.directory 
                      ? "bg-green-500/10 text-green-400" 
                      : "bg-white/5 text-gray-400"
                  )}
                    title={connections.directory ? 'Directory Connected' : 'Directory Not Connected'}
                  >
                    <div className={cn("w-1.5 h-1.5 rounded-full", connections.directory ? "bg-green-400" : "bg-gray-500")} />
                    <Globe className="w-3 h-3" />
                    <span>Dir</span>
                  </div>
                </div>
              </div>
              
              {/* Quick Links */}
              <div className="p-4 border-b border-white/10">
                <h3 className="text-xs font-medium text-gray-400 mb-3">Quick Links</h3>
                <div className="space-y-1">
                  <a
                    href="/settings/security"
                    className="flex items-center gap-2 p-2 rounded-lg hover:bg-white/5 transition-colors text-gray-400 hover:text-white"
                    data-testid="security-settings-link"
                  >
                    <Shield className="w-4 h-4" />
                    <span className="text-sm">Security Settings</span>
                  </a>
                </div>
              </div>
              
              {/* Drafts Section */}
              {drafts.length > 0 && (
                <div className="p-4 border-b border-white/10">
                  <h3 className="text-xs font-medium text-gray-400 mb-3 flex items-center gap-2">
                    <FileText className="w-3 h-3" />
                    Resume Drafts
                  </h3>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {drafts.map(draft => (
                      <div
                        key={draft.id}
                        className="group flex items-center gap-2 p-2 rounded-lg bg-amber-500/10 border border-amber-500/20 hover:bg-amber-500/20 transition-colors"
                      >
                        <button
                          onClick={() => handleWizardSelect(draft.wizard_type, draft)}
                          className="flex-1 flex items-center gap-2 text-left min-w-0"
                          data-testid={`draft-${draft.id}`}
                        >
                          <FileText className="w-4 h-4 text-amber-400 shrink-0" />
                          <div className="min-w-0">
                            <p className="text-xs font-medium text-white truncate">
                              {getWizardName(draft.wizard_type)}
                            </p>
                            <p className="text-xs text-gray-500 truncate">
                              {formatTimeAgo(draft.updated_at)}
                            </p>
                          </div>
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteDraft(draft.id);
                          }}
                          className="p-1 opacity-0 group-hover:opacity-100 hover:bg-red-500/20 rounded transition-all"
                          data-testid={`delete-draft-${draft.id}`}
                        >
                          <Trash2 className="w-3 h-3 text-red-400" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Task History */}
              <div className="flex-1 overflow-y-auto p-4">
                <h3 className="text-xs font-medium text-gray-400 mb-3 flex items-center gap-2">
                  <History className="w-3 h-3" />
                  Recent Tasks
                </h3>
                
                {loading ? (
                  <div className="space-y-2">
                    {[1, 2, 3].map(i => (
                      <div key={i} className="h-12 bg-white/5 rounded-lg animate-pulse" />
                    ))}
                  </div>
                ) : taskHistory.length > 0 ? (
                  <div className="space-y-2">
                    {taskHistory.map(task => (
                      <div
                        key={task.id}
                        className="flex items-center gap-3 p-2 rounded-lg bg-white/5 border border-white/5"
                      >
                        <div className={cn(
                          "w-6 h-6 rounded-lg flex items-center justify-center",
                          task.success ? "bg-green-500/20" : "bg-red-500/20"
                        )}>
                          <CheckCircle className={cn(
                            "w-3 h-3",
                            task.success ? "text-green-400" : "text-red-400"
                          )} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-white truncate">
                            {task.tool_name?.replace('_', ' ') || task.action}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {formatTimeAgo(task.created_at)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <History className="w-8 h-8 mx-auto mb-2 text-gray-600" />
                    <p className="text-sm text-gray-500">No recent tasks</p>
                    <p className="text-xs text-gray-600 mt-1">Tasks you complete will appear here</p>
                  </div>
                )}
              </div>
            </motion.aside>
          )}
        </AnimatePresence>
        
        {/* Main Content - Wizard Browser */}
        <main className="flex-1 flex flex-col overflow-hidden pt-4">
          {/* Header */}
          <div className="px-6 py-4 border-b border-white/10 mt-2">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-slate-400/30 to-blue-500/30 flex items-center justify-center">
                  <LayoutGrid className="w-6 h-6 text-slate-300" />
                </div>
                <div className="flex-1">
                  <h1 className="text-xl font-heading font-bold text-white">
                    AGI Workhorse™
                  </h1>
                  <p className="text-sm text-gray-400">
                    Where Artificial General Intelligence goes to work.
                  </p>
                </div>
                <button
                  onClick={() => setShowLearnMore(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-400/10 border border-slate-400/20 text-slate-300 text-sm hover:bg-slate-400/20 transition-colors"
                  data-testid="learn-more-btn"
                >
                  <HelpCircle className="w-4 h-4" />
                  <span>Learn More</span>
                </button>
              </div>
              
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search CRM Tasks..."
                  className="w-full pl-12 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-primary/50"
                  data-testid="wizard-search-input"
                />
              </div>
            </div>
          </div>
          
          {/* Wizard Grid */}
          <div className="flex-1 overflow-y-auto p-6">
            <div className="max-w-4xl mx-auto space-y-8">
              {/* Workspace Launch Card - scrolls with content */}
              <div className="mb-2">
                <WorkspaceLaunchCard />
              </div>
              {filteredCategories.map(category => {
                const colors = getCategoryColor(category.color);
                const CategoryIcon = category.icon;
                
                return (
                  <div key={category.id}>
                    <div className="flex items-center gap-3 mb-4">
                      <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center", colors.bg)}>
                        <CategoryIcon className={cn("w-4 h-4", colors.text)} />
                      </div>
                      <div>
                        <h2 className="text-lg font-semibold text-white">{category.name}</h2>
                        <p className="text-sm text-gray-400">{category.description}</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {category.wizards.map(wizard => {
                        const WizardIcon = wizard.icon;
                        const isActive = wizard.status === 'active';
                        
                        return (
                          <button
                            key={wizard.id}
                            onClick={() => isActive && handleWizardSelect(wizard.id)}
                            disabled={!isActive}
                            className={cn(
                              "flex items-center gap-3 p-4 rounded-xl border transition-all text-left group",
                              isActive 
                                ? `${colors.bg} ${colors.border} ${colors.hover} hover:scale-[1.02]` 
                                : "bg-white/5 border-white/5 opacity-50 cursor-not-allowed"
                            )}
                            data-testid={`wizard-${wizard.id}`}
                          >
                            <div className={cn(
                              "w-10 h-10 rounded-xl flex items-center justify-center transition-colors",
                              isActive ? colors.bg : "bg-white/5",
                              isActive && "group-hover:bg-white/10"
                            )}>
                              <WizardIcon className={cn("w-5 h-5", isActive ? colors.text : "text-gray-500")} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={cn("font-medium truncate", isActive ? "text-white" : "text-gray-500")}>
                                {wizard.name}
                              </p>
                              <p className="text-xs text-gray-500 truncate">{wizard.description}</p>
                            </div>
                            {isActive && (
                              <ChevronRight className={cn("w-4 h-4 transition-transform group-hover:translate-x-1", colors.text)} />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
              
              {filteredCategories.length === 0 && (
                <div className="text-center py-16">
                  <Search className="w-12 h-12 mx-auto mb-4 text-gray-600" />
                  <p className="text-lg text-gray-400">No workhorses found for "{searchQuery}"</p>
                  <p className="text-sm text-gray-500 mt-2">Try a different search term</p>
                </div>
              )}
            </div>
          </div>
        </main>
        
        {/* Active Wizard Modal */}
        {activeWizard && (
          <WizardController
            activeWizard={activeWizard}
            onClose={closeWizard}
            onSuccess={handleWizardSuccess}
            initialData={wizardInitialData}
            conversationId={null}
          />
        )}
        
        {/* Learn More Modal */}
        <AnimatePresence>
          {showLearnMore && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 pt-20 overflow-y-auto"
              onClick={() => setShowLearnMore(false)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-[#12121a] rounded-2xl border border-white/10 max-w-3xl w-full max-h-[80vh] overflow-y-auto my-4"
              >
                {/* Header */}
                <div className="sticky top-0 bg-[#12121a] px-6 py-4 border-b border-white/10 flex items-center justify-between z-10">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-slate-400/20 flex items-center justify-center">
                      <Info className="w-5 h-5 text-slate-300" />
                    </div>
                    <h2 className="text-xl font-bold text-white">What Is an AGI Workhorse™?</h2>
                  </div>
                  <button
                    onClick={() => setShowLearnMore(false)}
                    className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-400" />
                  </button>
                </div>
                
                {/* Content */}
                <div className="p-6 space-y-6">
                  {/* Intro */}
                  <div className="bg-gradient-to-br from-slate-400/10 to-blue-500/5 rounded-xl p-5 border border-slate-400/20">
                    <p className="text-gray-300 leading-relaxed">
                      An <span className="text-yellow-400 font-semibold">AGI Workhorse™</span> is an execution-driven Artificial General Intelligence system designed to perform real business tasks directly inside your:
                    </p>
                    <ul className="mt-3 space-y-2">
                      <li className="flex items-center gap-2 text-cyan-400"><Database className="w-4 h-4" /> CRM</li>
                      <li className="flex items-center gap-2 text-purple-400"><Package className="w-4 h-4" /> AI Horse-Powered POD</li>
                      <li className="flex items-center gap-2 text-green-400"><Globe className="w-4 h-4" /> AI Horse-Powered Directory</li>
                    </ul>
                    <div className="mt-4 pt-4 border-t border-white/10">
                      <p className="text-gray-400 text-sm">Unlike a chatbot that only generates responses, an AGI Workhorse <span className="text-white font-medium">executes structured actions</span> across your live systems.</p>
                      <p className="text-yellow-400 font-semibold mt-2">It doesn't just suggest what to do. It does it.</p>
                    </div>
                  </div>
                  
                  {/* What it Does */}
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-4">What the AGI Workhorse Does</h3>
                    <p className="text-gray-400 mb-4">Your AGI Workhorse operates inside your infrastructure and handles the heavy lifting of daily operations:</p>
                    
                    <div className="grid gap-4">
                      {/* CRM */}
                      <div className="bg-cyan-500/10 rounded-xl p-4 border border-cyan-500/20">
                        <h4 className="text-cyan-400 font-semibold mb-3 flex items-center gap-2">
                          <span className="w-3 h-3 rounded bg-cyan-500"></span> CRM Execution
                        </h4>
                        <ul className="grid grid-cols-2 gap-2 text-sm text-gray-300">
                          <li>• Find, update, and manage leads</li>
                          <li>• Send SMS and email communications</li>
                          <li>• Create opportunities and move pipeline stages</li>
                          <li>• Generate invoices, proposals, and payment links</li>
                          <li>• Deploy workflows and automations</li>
                          <li>• Trigger campaigns</li>
                          <li>• Generate performance reports</li>
                        </ul>
                      </div>
                      
                      {/* POD */}
                      <div className="bg-purple-500/10 rounded-xl p-4 border border-purple-500/20">
                        <h4 className="text-purple-400 font-semibold mb-3 flex items-center gap-2">
                          <span className="w-3 h-3 rounded bg-purple-500"></span> POD Operations
                        </h4>
                        <ul className="grid grid-cols-2 gap-2 text-sm text-gray-300">
                          <li>• Create and route department orders</li>
                          <li>• Launch marketing and creative tasks</li>
                          <li>• Track revisions and fulfillment</li>
                          <li>• Manage operational workflows</li>
                        </ul>
                      </div>
                      
                      {/* Directory */}
                      <div className="bg-green-500/10 rounded-xl p-4 border border-green-500/20">
                        <h4 className="text-green-400 font-semibold mb-3 flex items-center gap-2">
                          <span className="w-3 h-3 rounded bg-green-500"></span> Directory Control
                        </h4>
                        <ul className="grid grid-cols-2 gap-2 text-sm text-gray-300">
                          <li>• Create and update listings</li>
                          <li>• Launch offers and lead magnets</li>
                          <li>• Route inbound leads into CRM</li>
                          <li>• Deploy review systems</li>
                          <li>• Monitor listing performance</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  
                  {/* Why Called Workhorse */}
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-4">Why It's Called a "Workhorse"</h3>
                    <p className="text-gray-400 mb-3">Because it is built for:</p>
                    <div className="flex flex-wrap gap-2 mb-4">
                      <span className="px-3 py-1 rounded-full bg-yellow-500/20 text-yellow-400 text-sm font-medium">Endurance</span>
                      <span className="px-3 py-1 rounded-full bg-yellow-500/20 text-yellow-400 text-sm font-medium">Precision</span>
                      <span className="px-3 py-1 rounded-full bg-yellow-500/20 text-yellow-400 text-sm font-medium">Scale</span>
                      <span className="px-3 py-1 rounded-full bg-yellow-500/20 text-yellow-400 text-sm font-medium">Structured execution</span>
                    </div>
                    <p className="text-gray-400 mb-3">The AGI Workhorse combines:</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm text-gray-300">
                      <div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Artificial General Intelligence</div>
                      <div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Structured task engines</div>
                      <div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Secure API integrations</div>
                      <div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Context memory</div>
                      <div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Risk validation</div>
                      <div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Audit logging</div>
                    </div>
                    <p className="text-gray-300 mt-4">So it can operate inside your business systems <span className="text-yellow-400">responsibly</span> and <span className="text-yellow-400">powerfully</span>.</p>
                  </div>
                  
                  {/* Built for Control */}
                  <div className="bg-gradient-to-br from-primary/10 to-purple-500/10 rounded-xl p-5 border border-primary/20">
                    <h3 className="text-lg font-semibold text-white mb-3">Built for Power. Designed for Control.</h3>
                    <p className="text-gray-400 mb-3">Every AGI Workhorse operates under:</p>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="flex items-center gap-2 text-gray-300"><Shield className="w-4 h-4 text-primary" /> Tenant-level isolation</div>
                      <div className="flex items-center gap-2 text-gray-300"><Shield className="w-4 h-4 text-primary" /> Encrypted integrations</div>
                      <div className="flex items-center gap-2 text-gray-300"><Shield className="w-4 h-4 text-primary" /> Policy-gated execution</div>
                      <div className="flex items-center gap-2 text-gray-300"><Shield className="w-4 h-4 text-primary" /> Risk-tier confirmations</div>
                      <div className="flex items-center gap-2 text-gray-300"><Shield className="w-4 h-4 text-primary" /> Full audit logging</div>
                      <div className="flex items-center gap-2 text-gray-300"><Shield className="w-4 h-4 text-primary" /> Kill-switch protection</div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-white/10">
                      <p className="text-white font-medium">You get automation without chaos.</p>
                      <p className="text-yellow-400 font-semibold">Power without losing control.</p>
                    </div>
                  </div>
                  
                  {/* How It Works */}
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-4">How It Works</h3>
                    <div className="space-y-3">
                      {[
                        { num: 1, text: 'You give a command.' },
                        { num: 2, text: 'The AGI Workhorse evaluates inputs and risk level.' },
                        { num: 3, text: 'It either executes immediately or opens a structured task wizard.' },
                        { num: 4, text: 'It performs the action inside your CRM, POD, or Directory.' },
                        { num: 5, text: 'It logs and confirms the result.' }
                      ].map(step => (
                        <div key={step.num} className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-yellow-500/20 flex items-center justify-center text-yellow-400 font-bold text-sm">
                            {step.num}
                          </div>
                          <p className="text-gray-300">{step.text}</p>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 p-4 bg-white/5 rounded-xl">
                      <p className="text-gray-400 text-sm">No digging through menus. No switching platforms. No manual repetition.</p>
                    </div>
                  </div>
                  
                  {/* CTA */}
                  <div className="text-center pt-4">
                    <h3 className="text-xl font-bold text-white mb-2">Deploy Your AGI Workhorse™</h3>
                    <p className="text-gray-400 mb-4">Turn your business systems into a workforce.</p>
                    <Button
                      onClick={() => setShowLearnMore(false)}
                      className="bg-yellow-600 hover:bg-yellow-700 text-white px-8"
                    >
                      Get Started
                    </Button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </DashboardLayout>
  );
}
